import { isAxiosError } from 'axios';
import type { AxiosError } from 'axios';
import type { QueryClient } from '@tanstack/react-query';
import { captureApiError, shouldCaptureApiError, type ExtraTags } from './sentryCapture';
import { networkReportScope } from './networkReportScope';
import { hasIndependentNetworkConsumer, networkPromiseGroup } from './networkPromiseGroup';

// Axios rejects before TanStack decides to retry. Yield one task so the cache
// can claim that exact error instance; direct callers keep the fallback report.
const pending = new WeakMap<AxiosError, ReturnType<typeof setTimeout>>();

export function cancelPendingNetworkReport(error: unknown): void {
  if (!isAxiosError(error)) return;
  const timer = pending.get(error);
  if (timer === undefined) return;
  clearTimeout(timer);
  pending.delete(error);
}

export function scheduleNetworkReport(error: AxiosError): void {
  if (!shouldCaptureApiError(error)) return;
  const scope = error.config?._sentryReportScope;
  if (scope) {
    scope.record(error);
    return;
  }
  cancelPendingNetworkReport(error);
  pending.set(
    error,
    setTimeout(() => {
      pending.delete(error);
      const group = networkPromiseGroup(error);
      if (group) group.reportFailures({});
      else captureApiError(error);
    }, 0)
  );
}

export function captureFinalNetworkError(error: unknown, tags: ExtraTags, owner: object): boolean {
  const scope = networkReportScope(owner);
  const claimedGroup = networkPromiseGroup(error, owner);
  const group = claimedGroup?.discarded ? undefined : claimedGroup;
  if (scope && !group && isAxiosError(error) && !error.response && shouldCaptureApiError(error)) {
    scope.record(error);
  }
  scope?.reportFailures({ ...tags, user_visible: 'true', retry_outcome: 'failed' });
  group?.reportFailures({ ...tags, user_visible: 'true', retry_outcome: 'failed' });
  if (!isAxiosError(error) || error.response) return false;
  cancelPendingNetworkReport(error);
  if (shouldCaptureApiError(error) && !scope && !group) {
    captureApiError(error, { ...tags, user_visible: 'true', retry_outcome: 'failed' });
  }
  return true;
}

export function discardNetworkReports(error: unknown, owner: object): void {
  cancelPendingNetworkReport(error);
  const scope = networkReportScope(owner);
  if (
    hasIndependentNetworkConsumer(error) &&
    isAxiosError(error) &&
    !error.response &&
    shouldCaptureApiError(error)
  ) {
    if (scope) {
      scope.record(error);
      scope.reportFailures({ user_visible: 'true' });
    } else captureApiError(error, { user_visible: 'true' });
  }
  scope?.discard();
  networkPromiseGroup(error, owner)?.discard();
}

export function attachNetworkReporting(client: QueryClient): void {
  client.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'failed') {
      discardNetworkReports(event.action.error, event.query);
    }
  });
  client.getMutationCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'failed') {
      discardNetworkReports(event.action.error, event.mutation);
    }
  });
}
